import type { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto'
import type { DraggableLocation } from 'react-beautiful-dnd'

import type { DRAG_DROP_DROPPABLE_ID } from '@/constant/ui/makeStep2'
import type { AchievementReport } from '@/dto/AchievementAnalysis/AchievementReport.dto.ts'
import type { RawMakeProblemT } from '@/page/MakeCustomWorkbook/context/abstract/baseMakeCustomWorkbookContext'

export class CustomEventService {
  static eventNames = {
    // 프로덕트 내에서 사용하는 이벤트
    내교재PDF생성상태: 'CUSTOM_WORKBOOK_NOTIFICATION',
    TOSS_WIDGET_LOADED: 'TOSS_WIDGET_LOADED',
    스쿨플랫계약서생성상태: 'SCHOOLFLAT_CONTRACT_NOTIFICATION',
    유형분석보고서생성상태: 'ACHIEVEMENT_REPORT_NOTIFICATION',
    커스텀시그니처교재썸네일생성상태: 'CUSTOM_SIGNATURE_WORKBOOK_THUMBNAIL_NOTIFICATION',
    커스텀교재썸네일생성상태: 'CUSTOM_WORKBOOK_THUMBNAIL_NOTIFICATION',
    COVER_EDITOR_IMAGE_FILE_INPUT_CHANGED: 'COVER_EDITOR_IMAGE_FILE_INPUT_CHANGED',

    // 앱과 통신하며 사용하는 이벤트
    onQRCodeReceived: 'onQRCodeReceived',
    MAKE_LESSON_MATERIAL_STEP2_DND_REQUEST_PROBLEM:
      'MAKE_LESSON_MATERIAL_STEP2_DND_REQUEST_PROBLEM',
    MAKE_LESSON_MATERIAL_STEP2_DND_RESPONSE_PROBLEM:
      'MAKE_LESSON_MATERIAL_STEP2_DND_RESPONSE_PROBLEM',
    SCROLL_TO_SCROLL_TARGET_ID: 'SCROLL_TO_SCROLL_TARGET_ID',
  } as const

  static dispatch<T = any>(eventName: keyof (typeof CustomEventService)['eventNames'], detail: T) {
    window.dispatchEvent(new CustomEvent(CustomEventService.eventNames[eventName], { detail }))
  }

  static listen<T = any>(
    eventName: keyof (typeof CustomEventService)['eventNames'],
    listener: (event: CustomEvent<T>) => void,
  ) {
    window.addEventListener<ValueOf<(typeof CustomEventService)['eventNames']>>(
      CustomEventService.eventNames[eventName],
      listener,
    )

    return () => {
      window.removeEventListener<ValueOf<(typeof CustomEventService)['eventNames']>>(
        CustomEventService.eventNames[eventName],
        listener,
      )
    }
  }

  static 내교재PDF생성상태 = {
    dispatch: (detail: res.NotificationWithStatus<{ id: WorkbookEntity.Workbook['id'] }>) => {
      return CustomEventService.dispatch('내교재PDF생성상태', detail)
    },
    listen: (
      listener: (
        event: CustomEvent<res.NotificationWithStatus<{ id: WorkbookEntity.Workbook['id'] }>>,
      ) => void,
    ) => {
      return CustomEventService.listen<
        res.NotificationWithStatus<{ id: WorkbookEntity.Workbook['id'] }>
      >('내교재PDF생성상태', listener)
    },
  } as const

  static 커스텀시그니처교재썸네일생성상태 = {
    dispatch: (detail: res.NotificationWithStatus<{ id: WorkbookEntity.Workbook['id'] }>) => {
      return CustomEventService.dispatch('커스텀시그니처교재썸네일생성상태', detail)
    },
    listen: (
      listener: (
        event: CustomEvent<res.NotificationWithStatus<{ id: WorkbookEntity.Workbook['id'] }>>,
      ) => void,
    ) => {
      return CustomEventService.listen<
        res.NotificationWithStatus<{ id: WorkbookEntity.Workbook['id'] }>
      >('커스텀시그니처교재썸네일생성상태', listener)
    },
  } as const

  static 커스텀교재썸네일생성상태 = {
    dispatch: (detail: res.NotificationWithStatus<{ id: WorkbookEntity.Workbook['id'] }>) => {
      return CustomEventService.dispatch('커스텀교재썸네일생성상태', detail)
    },
    listen: (
      listener: (
        event: CustomEvent<res.NotificationWithStatus<{ id: WorkbookEntity.Workbook['id'] }>>,
      ) => void,
    ) => {
      return CustomEventService.listen<
        res.NotificationWithStatus<{ id: WorkbookEntity.Workbook['id'] }>
      >('커스텀교재썸네일생성상태', listener)
    },
  } as const

  static TOSS_WIDGET_LOADED = {
    dispatch: () => {
      return CustomEventService.dispatch('TOSS_WIDGET_LOADED', null)
    },
    listen: (listener: () => void) => {
      return CustomEventService.listen('TOSS_WIDGET_LOADED', listener)
    },
  }

  static 스쿨플랫계약서생성상태 = {
    dispatch: (detail: res.NotificationWithStatus) => {
      return CustomEventService.dispatch('스쿨플랫계약서생성상태', detail)
    },
    listen: (listener: (event: CustomEvent<res.NotificationWithStatus>) => void) => {
      return CustomEventService.listen<res.NotificationWithStatus>(
        '스쿨플랫계약서생성상태',
        listener,
      )
    },
  } as const

  static 유형분석보고서생성상태 = {
    dispatch: (detail: res.NotificationWithStatus<{ id: AchievementReport['id'] }>) => {
      return CustomEventService.dispatch('유형분석보고서생성상태', detail)
    },
    listen: (
      listener: (
        event: CustomEvent<res.NotificationWithStatus<{ id: AchievementReport['id'] }>>,
      ) => void,
    ) => {
      return CustomEventService.listen<res.NotificationWithStatus<{ id: AchievementReport['id'] }>>(
        '유형분석보고서생성상태',
        listener,
      )
    },
  } as const

  static onQRCodeReceived = {
    listen: (listener: (event: CustomEvent<{ worksheetId: string; dateTime: string }>) => void) => {
      return CustomEventService.listen('onQRCodeReceived', listener)
    },
  }

  static COVER_EDITOR_IMAGE_FILE_INPUT_CHANGED = {
    event: (detail: { id: string; file: File | null }) => {
      return new CustomEvent('COVER_EDITOR_IMAGE_FILE_INPUT_CHANGED', { detail })
    },
    listen: (listener: (event: CustomEvent<{ id: string; file: File | null }>) => void) => {
      return CustomEventService.listen('COVER_EDITOR_IMAGE_FILE_INPUT_CHANGED', listener)
    },
    dispatch: (detail: { id: string; file: File | null }) => {
      CustomEventService.dispatch('COVER_EDITOR_IMAGE_FILE_INPUT_CHANGED', detail)
    },
  }

  static MAKE_LESSON_MATERIAL_STEP2_DND_REQUEST_PROBLEM = {
    event: (detail: {
      problemTreeKey: string
      source: Omit<DraggableLocation, 'droppableId'> & {
        droppableId: ValueOf<typeof DRAG_DROP_DROPPABLE_ID>
      }
      destination: Omit<DraggableLocation, 'droppableId'> & {
        droppableId: ValueOf<typeof DRAG_DROP_DROPPABLE_ID>
      }
    }) => {
      return new CustomEvent('MAKE_LESSON_MATERIAL_STEP2_DND_REQUEST_PROBLEM', { detail })
    },
    dispatch: (detail: {
      problemTreeKey: string
      source: Omit<DraggableLocation, 'droppableId'> & {
        droppableId: ValueOf<typeof DRAG_DROP_DROPPABLE_ID>
      }
      destination: Omit<DraggableLocation, 'droppableId'> & {
        droppableId: ValueOf<typeof DRAG_DROP_DROPPABLE_ID>
      }
    }) => {
      return CustomEventService.dispatch('MAKE_LESSON_MATERIAL_STEP2_DND_REQUEST_PROBLEM', detail)
    },
    listen: (
      listener: (
        event: CustomEvent<{
          problemTreeKey: string
          source: Omit<DraggableLocation, 'droppableId'> & {
            droppableId: ValueOf<typeof DRAG_DROP_DROPPABLE_ID>
          }
          destination: Omit<DraggableLocation, 'droppableId'> & {
            droppableId: ValueOf<typeof DRAG_DROP_DROPPABLE_ID>
          }
        }>,
      ) => void,
    ) => {
      return CustomEventService.listen('MAKE_LESSON_MATERIAL_STEP2_DND_REQUEST_PROBLEM', listener)
    },
  }

  static MAKE_LESSON_MATERIAL_STEP2_DND_RESPONSE_PROBLEM = {
    event: (detail: {
      problem: RawMakeProblemT

      destination: Omit<DraggableLocation, 'droppableId'> & {
        droppableId: ValueOf<typeof DRAG_DROP_DROPPABLE_ID>
      }
    }) => {
      return new CustomEvent('MAKE_LESSON_MATERIAL_STEP2_DND_RESPONSE_PROBLEM', { detail })
    },
    dispatch: (detail: {
      problem: RawMakeProblemT

      destination: Omit<DraggableLocation, 'droppableId'> & {
        droppableId: ValueOf<typeof DRAG_DROP_DROPPABLE_ID>
      }
    }) => {
      return CustomEventService.dispatch('MAKE_LESSON_MATERIAL_STEP2_DND_RESPONSE_PROBLEM', detail)
    },
    listen: (
      listener: (
        event: CustomEvent<{
          problem: RawMakeProblemT

          destination: Omit<DraggableLocation, 'droppableId'> & {
            droppableId: ValueOf<typeof DRAG_DROP_DROPPABLE_ID>
          }
        }>,
      ) => void,
    ) => {
      return CustomEventService.listen('MAKE_LESSON_MATERIAL_STEP2_DND_RESPONSE_PROBLEM', listener)
    },
  }

  static SCROLL_TO_SCROLL_TARGET_ID = {
    dispatch: (detail: { scrollTargetId: number | string }) => {
      return CustomEventService.dispatch('SCROLL_TO_SCROLL_TARGET_ID', detail)
    },
    listen: (listener: (event: CustomEvent<{ scrollTargetId: number | string }>) => void) => {
      return CustomEventService.listen('SCROLL_TO_SCROLL_TARGET_ID', listener)
    },
  }
}
